import { FIGURE_SVG_EDITOR } from '../../utils/roles';
/**
 * figureText — 그림 API 의 오류·사유를 화면 말로 옮긴다.
 *
 * 계약 §1-4 의 오류표 전부를 다룬다. 상태코드만 보고 「실패했습니다」로 뭉개면 사용자는
 * **다시 시도할지 포기할지** 판단할 근거를 잃는다 — 여기 여덟 갈래는 대응이 서로 다르다.
 * 판별은 **코드로만** 한다(httpStatus + data.reason). message 문자열로 가르면 백엔드가
 * 문구를 다듬는 날 조용히 어긋난다.
 */

/**
 * 계약 §1-5 이력 source 코드 — 서버가 source_name 을 함께 주므로 그것이 우선이고, 여기는 폴백이다.
 *
 * 700(삭제)은 §1-5 표가 아니라 **§8(2026-08-29 변경)** 에만 적혀 있고, 800(생성)은
 * **§9-2(2026-08-30)** 에만 있다 — 정본의 본문 표는 갱신되지 않아 100~600 만 있다.
 * 그 표만 보고 옮기면 둘이 빠지고 화면에 「사유 700」·「사유 800」이 그대로 뜬다.
 */
export const FIGURE_SOURCE_NAMES = {
  100: '관측',
  200: '변환',
  300: '재요청',
  400: '편집',
  500: '되돌리기',
  600: '제거',
  700: '삭제',
  800: '생성',
  // 2026-09-04 계약 §10(회신 §6) — 올린 이미지로 만든 두 판. 둘 다 그림이 픽셀이 되고 원본이 보관된다.
  900: '등록', // POST …/figures — 붙여넣은 이미지를 새 그림으로
  950: '교체', // PATCH … {image} — 기존 그림을 올린 이미지로
};

/**
 * 삭제 버전(source 700)인가. 이 버전에는 [이 버전으로](restore)를 내지 않는다 —
 * 삭제된 그림의 이력은 「퇴장(조회 불가)」이라 되돌리면 무엇이 되는지 계약에 없다(회신 §2 Q8).
 * 누르면 무슨 일이 날지 모르는 버튼을 내지 않는다는 이 저장소의 규칙(figureCapabilities 주석)과 같다.
 */
export function isFigureDeletedVersion(version) {
  return Number(version?.source) === 700;
}

/**
 * 이력 한 줄이 **무엇을 낼 수 있는가** — 조건이 넷으로 갈려서 한자리에 모은다.
 *
 * figureFaces 와 같은 이유다: 흩어진 조건은 시험으로 덮을 수 없고, 어긋나도 화면은 멀쩡해
 * 보인다. 실제로 2026-08-31 에 그 어긋남 하나가 저장을 영영 잠갔다(figureFaces 머리주석).
 *
 * ── 어느 갈래든 고르기는 **서버로 나가지 않는다** (오너 원칙 2026-09-01) ──
 * 「고르기」는 화면 안의 일이고, 서버로 나가는 것은 [저장] 하나뿐이다. 이력이 append-only 라
 * (계약 §1-5) 고르는 행위가 서버로 나가면 **둘러보는 것만으로 버전이 쌓인다** — 2026-08-30 에
 * 실제로 그랬고(v4~v13 이 전부 사유 500), 그래서 되돌리기 API 를 즉시 호출로 쓰지 않기로 했다.
 *
 *   · `none`     서버에 저장된 그 버전이다. 「지금」이라 부를 수 있는 것은 하나뿐이다.
 *   · `blocked`  고를 수 없다 — 삭제 버전(700, 되돌리면 무엇이 되는지 계약에 없다 · 회신 §2 Q8)
 *                이거나, 사진 버전인데 **돌아갈 보관 원본이 없다**(그때 `spec:null` 은 409).
 *   · `load`     좌표·식이 있다. `POST /figspec/render` 로 그려 보고 폼으로 가져온다 →
 *                [저장]이 `PATCH {spec}` 을 보낸다.
 *   · `restoreContent` spec 은 없는데 **그 판의 실제 내용이 서버에 있다**(has_content=true ·
 *                계약 「판 내용 스냅샷」 09-01 — 트레이스·픽셀 판). 미리보기는 GET
 *                history/{version} 의 src 로 그리고 → [저장]이 restore 를 1회 보낸다(그 내용을
 *                싣는 PATCH 가 계약에 없어 이 길뿐이다 · figureApi.restoreFigure 주석).
 *   · `original` 사진이다. 그 버전의 내용이 남아 있지 않아 **돌아갈 곳은 서버가 보관한
 *                원본 한 장**이고(§4-2 — 보관은 픽셀→SVG 첫 전환 때 한 번뿐), 그래서 미리보기도
 *                그 한 장을 그린다 → [저장]이 `PATCH {spec:null}` 을 보낸다.
 *
 * has_content 키가 없는 응답(구 배포·스테이징)에서는 두 플래그가 다 거짓이 되어 **현행(보관
 * 원본) 동작 그대로** 물러난다 — 트레이스 판이 「같은 사진」으로 보이는 것이 그때는 옳은 폴백이다.
 *
 * @param opts.canPickOriginal 사진으로 되돌아가는 저장이 성립하는가(caps.canDropSpec).
 * @param opts.canShowOriginal 그 원본을 화면이 실제로 받아 그릴 수 있는가.
 */
export function figureVersionRow(version, currentVersion, opts = {}) {
  const { canPickOriginal = false, canShowOriginal = false } = opts;
  const isCurrent = version?.version != null && Number(version.version) === Number(currentVersion);
  const isDeleted = isFigureDeletedVersion(version);
  const hasSpec = version?.spec != null;
  // spec 있는 판은 렌더로 그리고 PATCH 로 가므로 has_content 를 볼 일이 없다 — spec 없는 판에서만 가른다.
  // 삭제 판(700)은 제외한다 — 계약상 삭제 이력은 퇴장이라 GET history/{version} 이 404 다.
  // 열어 두면 [보기]가 반드시 실패하는 요청을 쏘고, 실패 칸이 이 화면이 막아 둔 restore 를 약속한다.
  const hasContent = !hasSpec && !isDeleted && version?.has_content === true;
  // 도입(09-01) 전 판 — 서버가 「내용이 소실됐다」고 명시한 것만. 키가 안 온 것은 모르는 것이다.
  const lostContent = !hasSpec && version?.has_content === false;
  return {
    isCurrent,
    isDeleted,
    hasSpec,
    hasContent,
    lostContent,
    // 저장 없이 볼 수 있는가 — 좌표·식은 렌더로, 내용 있는 판은 GET history/{version} 의 src 로,
    // 사진은 보관 원본으로 그린다. hasContent 를 canShowOriginal 에 묶지 않는다 — 원본 없이도 그린다.
    canPreview: hasSpec || hasContent || canShowOriginal,
    action: isCurrent ? 'none'
      : isDeleted ? 'blocked'
        : hasSpec ? 'load'
          : hasContent ? 'restoreContent'
            : canPickOriginal ? 'original' : 'blocked',
  };
}

/**
 * 도입(09-01) 전 판 — 그 판의 실제 그림이 서버에 남아 있지 않다(계약 「판 내용 스냅샷」의 한계,
 * has_content=false). **같은 사진이 또 보이는 까닭을 화면이 말해야 한다** — 안 하면 목록이
 * 고장으로 읽힌다(빈칸·중복은 고장으로 읽힌다는 이 저장소의 규칙 · FigureHistoryList 주석).
 */
export const FIGURE_LOST_CONTENT_TEXT =
  '이 판의 실제 그림은 남아 있지 않습니다(스냅샷 도입 전 판) — 보관 원본으로만 돌아갑니다';

/**
 * 고치는 칸이 **무엇을 내는가** — 「숫자·식」과 「그림 속 글자」가 한 자리를 쓴다 (2026-09-01).
 *
 * 오너 지시 「합쳐 줄래 — 상황에 따라 필요한 요소가 나오도록」. 종전에는 두 칸이 따로 서서,
 * 사진을 따라 그린 그림에서는 앞칸이 「여기엔 고칠 것이 없습니다」만 말하고 실제로 할 수 있는
 * 일은 아래 다른 상자에 있었다.
 *
 * **함수로 뽑는 이유는 figureFaces·figureVersionRow 와 같다** — 이 판정이 컴포넌트 안에
 * 세 자리(칸 제목·머리줄 버튼·본문)로 흩어지면 어긋나도 화면은 멀쩡해 보인다. 실제로
 * 2026-08-31 에 그런 어긋남 하나가 저장을 영영 잠갔다(figureFaces 머리주석).
 *
 * 규칙 셋:
 *   · 글자 탭은 **그림 안에 글자가 있을 때만** 선다. 없는데 내면 눌러 봐야 409 `not_traced` 다.
 *   · 좌표·식 탭은 **트레이스인데 명세가 없을 때만** 빠진다. 빈 폼을 내면 사람이 그것을 채워
 *     저장하고, 그 `PATCH {spec}` 이 127KB 짜리 트레이스를 선 몇 개짜리 도형으로 갈아 없앤다.
 *   · 설 수 있는 것이 하나뿐이면 **탭을 보이지 않는다**(showTabs) — 고를 것이 하나인데 고르라고
 *     내밀지 않는다. 그때 `active` 는 고른 값과 무관하게 그 하나로 굳는다: 고른 뒤 그림이 바뀌어
 *     (이력에서 명세를 가져오거나 되돌려) 그 탭이 사라지면 빈 칸을 그리게 된다.
 *
 * @param traceCount 그림 안 글자 수(figureApi.readTraceTexts 의 길이). OCR 에서는 늘 0이다.
 * @param hasSpec 고칠 명세가 손에 있는가 — 창의 `draft != null`. **`item.spec` 이 아니다**:
 *   이력에서 좌표·식 판을 가져오면 draft 가 생겨 폼이 다시 열려야 한다.
 * @param picked 사람이 탭에서 고른 값('spec' | 'trace'). 설 수 없는 탭이면 무시된다.
 */
export function figureEditTabs({ traceCount = 0, hasSpec = false, picked = 'trace' } = {}) {
  const canTrace = traceCount > 0;
  const canSpec = !(canTrace && !hasSpec);
  return {
    canSpec,
    canTrace,
    showTabs: canSpec && canTrace,
    active: !canTrace ? 'spec' : !canSpec ? 'trace' : picked,
  };
}

export function figureSourceName(version) {
  if (!version) return '';
  if (version.source_name) return String(version.source_name);
  return FIGURE_SOURCE_NAMES[version.source] || `사유 ${version.source ?? '?'}`;
}

/**
 * 오류 하나를 { text, reason, issues, recoverable } 로 옮긴다.
 * - reason: 화면이 분기에 쓰는 기계 코드(문구가 아니라 이것으로 판단한다)
 * - issues: 서버가 쓴 한국어 한 줄들. **그대로 내보낸다** — 어느 원소가 왜 걸렸는지는 서버만 안다
 * - recoverable: 값을 고쳐 다시 시도할 여지가 있는가. false 면 창을 닫는 쪽이 맞다
 */
export function describeFigureError(err) {
  const status = err?.httpStatus;
  const reason = err?.data?.reason || null;
  const issues = Array.isArray(err?.data?.issues) ? err.data.issues : [];
  const serverMsg = err?.message || '';

  if (FIGURE_SVG_EDITOR && ['stale_revision', 'stale', 'spec_retired', 'no_original'].includes(reason)) {
    const messages = {
      stale_revision: '그림이 다른 곳에서 바뀌었습니다. 초안을 보관한 뒤 다시 읽어 주세요.',
      stale: '문제가 다른 곳에서 바뀌었습니다. 초안을 보관한 뒤 다시 읽어 주세요.',
      spec_retired: '이전 그림 편집 방식은 종료되었습니다. 새 SVG 편집기를 사용해 주세요.',
      no_original: '사용할 원본이 없습니다. AI 요청 창에 이미지를 올려 주세요.',
    };
    return { reason, recoverable: true, issues, text: messages[reason] };
  }
  if (status === 409 && reason === 'stale') {
    return {
      reason: 'stale',
      recoverable: false,
      issues: [],
      text: '내가 읽은 뒤 이 문제가 바뀌었습니다 — 다른 사람의 저장이거나, 이 창 밖에서 일어난 변경입니다. 창을 닫고 다시 여세요.',
    };
  }
  if (status === 409 && reason === 'invalid_spec') {
    return {
      reason: 'invalid_spec',
      recoverable: true,
      issues,
      // "무변경"을 반드시 말한다 — 저장이 반쯤 됐다고 오해하면 사용자가 값을 다시 짜 맞춘다
      text: '명세가 검증을 통과하지 못해 저장하지 않았습니다. 화면의 값은 그대로 두었습니다.',
    };
  }
  if (status === 409 && reason === 'no_original') {
    /**
     * 두 자리에서 온다 — [이 원본으로 되돌리기](`PATCH {spec:null}`)와, 2026-09-01 부터는
     * **[원본 사진]으로 건 AI 요청**(`reconvert {use_original:true}` · 계약 §9-4)이다.
     * 하는 일이 달라서 한쪽 말로만 적으면 다른 쪽에서 거짓이 된다(재요청 중에 「되돌릴 수
     * 없습니다」를 읽는 식). 그래서 **사실 한 줄 + 남은 길 한 줄**로 쓴다.
     *
     * recoverable 이다 — 바로 아래 `not_traced` 와 같은 근거다: **아무것도 바뀌지 않았고**
     * 화면의 값도 그대로다. 이 사유로 창을 잠그면(`fail` 의 `!recoverable` 갈래) 원본이 없다는
     * 이유 하나로 좌표 편집·저장·이력까지 통째로 잠긴다 — 그 길들은 원본과 무관하다.
     */
    return {
      reason: 'no_original',
      recoverable: true,
      issues: [],
      text: '이 그림에는 보관된 원본이 없습니다 — 원본으로 되돌리거나 원본으로 다시 그릴 수 없습니다. AI 요청은 [지금 그림]을 골라 다시 해 보세요.',
    };
  }
  if (status === 409 && reason === 'not_traced') {
    /**
     * 계약 §9-4 — 「트레이스 그림이 아니면 409 `not_traced`」.
     *
     * recoverable 이다: 아무것도 바뀌지 않았고 화면의 값도 그대로다. 이 사유로 창을 잠그면
     * (fail 의 `!recoverable` 갈래) 글자 하나 못 바꾼 것 때문에 그림 편집 전체가 닫힌다.
     * 남은 길을 함께 말한다 — 「할 수 없습니다」로 끝내면 사용자는 왜 칸이 떴는지 모른다.
     */
    return {
      reason: 'not_traced',
      recoverable: true,
      issues: [],
      text: '이 그림은 사진을 따라 그린 것이 아니라, 그림 속 글자를 고칠 수 없습니다.'
        + ' 먼저 [AI 요청] → [사진 따라 그리기]로 만들어야 합니다.',
    };
  }
  if (status === 409 && reason === 'in_progress') {
    return {
      reason: 'in_progress',
      recoverable: true,
      issues: [],
      text: '이 문제의 그림 작업이 이미 진행 중입니다 — 끝난 뒤 다시 시도하세요.',
    };
  }
  if (status === 409) {
    // reason 없는 409 — 옛 배포분과 섞여 돌 때의 안전망. 서버 message 를 그대로 보인다.
    return { reason: null, recoverable: false, issues: [], text: serverMsg || '다른 곳에서 먼저 바뀌었습니다 — 창을 닫고 다시 여세요.' };
  }
  if (status === 422) {
    return {
      reason: 'blank_source',
      recoverable: false,
      issues: [],
      // LLM 을 부르지도 않고 즉시 돌아온다 — 기다렸다 실패한 것이 아니라는 뜻을 담는다
      text: serverMsg || '원본이 백지이거나 원본 위치가 없어 AI 가 읽을 것이 없습니다.',
    };
  }
  if (status === 423) {
    return {
      reason: 'locked',
      recoverable: false,
      issues: [],
      text: '이미 등록된 문제이거나 종료된 프로젝트라 고칠 수 없습니다.',
    };
  }
  if (status === 404) {
    return {
      reason: 'not_found',
      recoverable: false,
      issues: [],
      text: '그 그림을 서버에서 찾지 못했습니다 — 본문이 바뀐 듯합니다. 창을 닫고 다시 여세요.',
    };
  }
  if (status === 413) {
    /**
     * 계약 §10(회신 §7) — `image` 디코드 크기가 2MB 를 넘었다. 서버 메시지에 **현재 크기(KB)** 가
     * 들어 있어 그대로 쓴다. 화면이 보내기 전에 같은 검사를 하지만(figureApi.parseImageDataUri),
     * 그 계산은 base64 길이로 재는 어림이라 서버 실측과 어긋날 수 있다 — 그 자리를 여기가 받는다.
     *
     * recoverable 이다: 아무것도 바뀌지 않았고, 사람이 더 작은 이미지를 골라 다시 하면 된다.
     */
    return {
      reason: 'too_large',
      recoverable: true,
      issues: [],
      text: `${serverMsg || '이미지가 너무 큽니다.'} 더 작게 잘라 올리거나 화면 캡처 범위를 좁혀 주세요.`,
    };
  }
  if (status === 400) {
    return {
      reason: 'bad_request',
      recoverable: false,
      issues: [],
      text: serverMsg || '보낼 수 없는 값입니다.',
    };
  }
  return { reason: null, recoverable: true, issues: [], text: serverMsg || '요청에 실패했습니다.' };
}

/**
 * 그림 하나에서 어떤 동작이 가능한지. 눌리면 반드시 실패하는 버튼을 내지 않기 위한 판정이다.
 * 계약 §4-2: spec:null 은 보관 원본이 있어야 하고(없으면 409), 이미 픽셀인 그림에는 400.
 */
export function figureCapabilities(item, target) {
  /**
   * **서버가 「없다」고 말한 것만 없는 것으로 친다.**
   *
   * 2026-08-30 두 번째 정정: 종전 `has_original === true` 는 서버가 그 키를 **주지 않은 경우**까지
   * 「없다」로 읽었다. 그 하나로 [AI 요청]과 [원래 사진으로]가 잠겼고, 오너가 두 번 보고했다
   * (「AI 요청 아직도 안 됨」 — 버튼 title 이 「보관된 원본이 없어…」였다).
   *
   * 이 저장소는 같은 상황에서 이미 반대로 하고 있다 — 폴더 잠금은 `is_mine` 이 안 실려 오면
   * **잠그지 않는다**. 모르는 것으로 막으면 서버가 필드를 늦게 붙이거나 이름을 바꾸는 순간
   * 멀쩡한 기능이 통째로 죽고, 화면은 그 이유를 「없어서」라고 **거짓으로** 말한다.
   * 확실히 실패하는 것만 막고, 모르는 것은 눌러서 서버가 판정하게 둔다(409 `no_original`).
   */
  const noOriginal = item?.has_original === false;
  const isPixel = item?.kind === 'pixel';
  const versions = Number(item?.history_version) || 0;
  return {
    /**
     * [원래 사진으로] — 계약 §4-2 가 400 을 내는 조건은 **「이미 픽셀인 그림」** 하나이고,
     * 409 를 내는 조건은 보관 원본이 없는 것이다. 그래서 판정도 그 둘을 그대로 본다.
     *
     * 2026-09-01 정정: 종전 조건은 `hasSpec`(= `item.spec != null`)이었다. 「명세가 있다」를
     * 「픽셀이 아니다」의 **대역**으로 쓴 것인데, 트레이스 그림이 그 대역을 깬다 —
     * `kind:'svg'` 인데 `spec:null` 이다(실측 61654: 그림은 SVG 로 `src` 에 실려 오고 명세는 없다).
     * 그 하나로 트레이스 결과가 마음에 안 들 때 **원본 사진으로 돌아가는 길이 창에서 통째로
     * 사라졌다** — 위 버튼도, 이력의 사진 줄도 전부 잠겼다. 서버는 받아 줄 요청인데 화면이
     * 먼저 막은 것이다. 같은 문제·같은 자리에서 `PATCH {spec:null}` 이 실제로 성공한 기록이
     * 이력에 남아 있다(v17 · 사유 600 「제거」).
     *
     * ⚠ 그렇다고 `!isPixel` **하나로** 갈면 이번엔 반대쪽이 닫힌다 — 계약 §6 의 「명세가 무효한
     * 그림」은 `kind:"pixel"` 로 오면서 **`spec` 은 그대로 들고 있다**(저장된 명세는 두고 표시만
     * 픽셀로 폴백한 상태). 그 그림에서 종전에 열려 있던 버튼이 닫힌다. 계약 §4-2 의
     * 「이미 `pixel`인 그림」이 `kind` 를 말하는지 「버릴 명세가 없다」를 말하는지 문서가 가르지
     * 않아 **우리는 모른다**(→ 질의 FE-20260901-trace-texts-and-figure-contract-gaps.md).
     * 모르는 것으로 막지 않는다는 이 함수의 원칙(바로 위 noOriginal 주석)대로 **둘 중 하나만
     * 참이면 연다** — 트레이스(svg·명세 없음)도 열리고, 명세 무효 픽셀도 종전대로 열린다.
     * 확실히 닫히는 것은 「픽셀이면서 버릴 명세도 없는」 그림 하나뿐이고, 그때는 400 이 확정이다.
     */
    canDropSpec: (item?.spec != null || item?.kind === 'svg') && !noOriginal,
    /**
     * [AI 요청] — **서버가 원본을 어디서 찾는지**가 대상마다 다르다.
     *
     * 2026-08-30 정정: 한때 `has_original` 하나로만 잠갔는데, 그러면 **한 번도 변환한 적 없는
     * 픽셀 그림이 영영 잠긴다** — 계약 §4-2 가 「픽셀 → SVG **첫 전환 때** 원본을 보관한다」라
     * 첫 전환 전에는 그 값이 늘 false 다. 오너 보고 「AI 재요청해서 다시 그릴 수가 없다」가 이것이다.
     *
     * · OCR(§3-3) — 서버가 **페이지에서 bbox 로 잘라** 쓴다. 요청에 이미지를 안 보내도 되고
     *   보관 원본과 무관하다. 원본이 백지면 422 로 오는데, 그때는 눌러 봐야 아는 것이라 막지 않는다.
     * · 은행(§4-3) — 우선순위가 `body.image` → **노드가 pixel 이면 그 src** → 이력 보관 원본.
     *   그래서 픽셀이면 보관 원본과 무관하게 되고, 도판은 보관 원본이 **없다고 확인된 때만** 막는다.
     */
    canReconvert: target === 'ocr' ? true : (isPixel || !noOriginal),
    // 되돌릴 버전이 있는가. version 1 은 「관측」(현재 상태를 처음 기록한 것)이라 그것뿐이면 바꿀 것이 없다
    canRestore: versions > 1,
  };
}

/**
 * 줄 맞춰보기(matchFigureSequence)·단건 대조(matchFigure) 실패를 화면 말로.
 *
 * 「어긋났습니다」로 뭉개지 않고 **몇 장 대 몇 장인지 · 몇 번째가 다른지**를 말한다.
 * 사용자가 다음에 할 일이 갈리기 때문이다 — 개수가 다르면 본문에서 그림을 지웠거나 더한 것이라
 * [저장]하면 풀리고, 자리가 바뀐 것이라면 저장만으로는 안 풀릴 수 있다.
 *
 * seq 를 주지 않으면(사유를 모르는 단건 실패) 일반 문구를 낸다.
 */
export function describeFigureMismatch(seq, target) {
  const tail = ' 순번이 어긋나면 서버는 다른 그림에 저장하고도 성공이라 답합니다 — 그래서 막았습니다.';
  if (seq?.reason === 'count') {
    /**
     * **개수가 어긋난 이유가 「직접 넣은 이미지」로 확정되는 경우** — 문서가 더 많고, 그 차이가
     * 전부 서버가 모르는 그림이다(seq.unknownInDoc). 여기서 화면에 따라 **할 말이 정반대**다.
     *
     * · OCR 검수 — 서버는 원문(payload)의 그림만 센다(계약 §10 · 회신 §5). 편집본에만 있는
     *   image 노드는 저장해도 목록에 들어오지 않는다. 그런데 종전 문구는 「먼저 [저장]하세요」였고,
     *   검수자는 이미 저장한 뒤에 이 창을 연 것이다 — **이미 한 일을 시키는 막다른 안내**였다.
     * · 문제은행 — 서버가 본문 Lexical 을 직접 센다. 저장하면 실제로 그 그림이 목록에 들어와
     *   대조가 맞는다. 여기서는 [저장]이 참말이다.
     *
     * 한 문구로 덮으면 한쪽이 반드시 거짓이 된다.
     */
    const added = seq.docCount - seq.serverCount;
    if (added > 0 && seq.unknownInDoc === added) {
      if (target === 'ocr') {
        // 2026-09-04 정정 — 저장할 때 자동 등록(registerNewFigures)하므로 이제 [저장]이 참말이다.
        // 종전 문구(「저장해도 달라지지 않습니다」)는 그 뒤로 거짓이 됐다.
        return `본문에 아직 등록되지 않은 이미지가 ${added}장 있어 서버 그림 수와 다릅니다 —`
          + ' 먼저 [저장]하면 그림으로 등록되어 편집할 수 있습니다.';
      }
      return `화면에는 그림이 ${seq.docCount}장인데 서버는 ${seq.serverCount}장으로 셉니다 —`
        + ` 본문에 더한 이미지 ${added}장을 서버가 아직 모릅니다.`
        + ' 먼저 [저장]하면 서버가 그 그림도 세고, 그때부터 편집할 수 있습니다.';
    }
    return `화면에는 그림이 ${seq.docCount}장인데 서버는 ${seq.serverCount}장으로 셉니다.${tail}`
      + ' 본문에서 그림을 지웠거나 더했다면 먼저 [저장]한 뒤 다시 열어 주세요.';
  }
  if (seq?.reason === 'kind') {
    return `${(seq.at ?? 0) + 1}번째 그림의 종류가 서버와 다릅니다(도판 ↔ 원본).${tail}`
      + ' 본문에서 그림 자리를 옮겼다면 먼저 [저장]한 뒤 다시 열어 주세요.';
  }
  if (seq?.reason === 'src') {
    // 회신 §2 Q5·Q6 으로 바이트 대조가 판정이 됐다 — 종류는 같은데 내용이 다르면 **다른 문서**다.
    // 「저장하라」고 말하지 않는다: 미저장분은 이 창을 열기 전에 이미 막았으므로, 남은 원인은
    // 내가 읽은 뒤 본문이 바뀐 것(또는 편집본)이고 그때 필요한 것은 저장이 아니라 다시 읽기다.
    return `${(seq.at ?? 0) + 1}번째 그림이 서버가 가진 그림과 내용이 다릅니다(종류는 같습니다).${tail}`
      + ' 창을 닫고 본문을 다시 읽은 뒤 열어 주세요.';
  }
  return `내가 누른 그림과 서버가 준 그림이 서로 다릅니다.${tail}`
    + ' 본문을 저장한 뒤 다시 열어 주세요.';
}

/**
 * 본문에 새로 들어온 이미지를 **서버 그림으로 등록하려다 못 한** 이유 — `registerNewFigures` 의 실패.
 *
 * 저장을 막는 말이므로 **왜 막혔는지와 무엇을 하면 되는지**를 함께 적는다. 등록하지 못한 이미지를
 * 그대로 저장하면 그것이 서버가 모르는 채 편집본에 남아 그 field 의 그림 편집이 잠긴다 —
 * 지금 고치려는 그 상태가 다시 만들어진다.
 */
export function describeRegisterFailure(reason, field) {
  const where = field === 'solution' ? '풀이' : '본문';
  if (reason === 'too_large') {
    return `${where}에 2MB 를 넘는 이미지가 있어 저장하지 않았습니다 — 그 이미지를 지우고 더 작게 잘라 넣어 주세요.`;
  }
  if (reason === 'bad_image') {
    return `${where}에 서버가 받지 않는 형식의 이미지가 있어 저장하지 않았습니다(PNG·JPEG·WEBP 만 됩니다)`
      + ' — 그 이미지를 지우고 다시 넣어 주세요.';
  }
  return `${where}의 이미지를 그림으로 등록하지 못해 저장하지 않았습니다 — 창을 닫고 다시 연 뒤 시도해 주세요.`;
}

/**
 * 본문에서 사라진 그림을 **서버에서도 지우려다 못 한** 이유 — `purgeRemovedFigures` 의 실패.
 *
 * 저장을 막는 말이므로 **왜 막혔는지와 무엇을 하면 되는지**를 함께 적는다. 종전 문구
 * 「그림은 [삭제]로 지워야 이력이 맞습니다 … 실행 취소로 되돌린 뒤」는 사유를 하나로 뭉갰고,
 * 그 처방이 가리키는 [삭제] 버튼이 같은 창의 순번 대조에 막혀 있어 순환이 됐다(오너 보고 2026-09-04).
 */
export function describePurgeFailure(reason, field) {
  const where = field === 'solution' ? '풀이' : '본문';
  // 같은 그림이 여럿인 경우는 이제 자리로 가른다(planFigureDeletions) — 여기 오는 것은 대조할 값이
  // 없는 그림(원본 페이지가 만료된 픽셀)이 여럿인데 일부만 사라진 경우뿐이다.
  if (reason === 'no_src') {
    return `${where}에 서버와 맞춰 볼 수 없는 그림(원본이 만료된 그림)이 여럿인데 일부만 지워져`
      + ' 어느 것인지 가릴 수 없습니다 — 저장하지 않았습니다. 실행 취소(⌘/Ctrl+Z)로 되돌려 주세요.';
  }
  return `${where}에서 사라진 그림을 서버에서 지우지 못해 저장하지 않았습니다 — 창을 닫고 다시 연 뒤 시도해 주세요.`;
}

/**
 * 삭제가 **중간에** 멈췄다 — 이미 지운 것은 서버에 남아 있다. 그 사실을 반드시 말한다:
 * 「저장에 실패했습니다」로만 끝내면 사람은 아무 일도 없었다고 믿고 같은 것을 다시 지운다.
 */
export function describePartialPurge(deletedCount, field, detail) {
  const where = field === 'solution' ? '풀이' : '본문';
  return `${where}의 그림 ${deletedCount}장을 서버에서 지운 뒤 멈췄습니다 — 본문은 저장하지 않았습니다.`
    + ` (${detail}) 창을 닫고 다시 연 뒤 남은 것을 이어서 지워 주세요.`;
}

/**
 * **누른 그림이 서버 목록에 없다** — 검수자가 본문에 직접 넣은 이미지가 이 갈래다(계약 §10 · 회신 §5).
 *
 * 종전에는 이 자리에 「서버 목록에서 이 그림(본문 N번째)을 찾지 못했습니다 — 본문이 바뀐 듯합니다.
 * 창을 닫고 다시 열어 주세요」가 떴다. 원인도 처방도 틀렸다: 본문이 바뀐 것이 아니라 **서버가
 * 원래 세지 않는 그림**이고, 다시 열어도 같은 화면이 나온다.
 *
 * `canRemove` 는 **이 창에서 뺄 수 있는가**다. 뺄 수 없으면(편집기가 마운트돼 있지 않은 등)
 * 그 길을 이름으로 부르지 않는다 — 없는 버튼을 부르면 그 자체가 막다른 안내가 된다.
 */
export function describeUnknownFigure({ field, target, canRemove, canRegister } = {}) {
  const where = field === 'solution' ? '풀이' : '본문';
  if (target !== 'ocr') {
    return `이 그림을 서버 목록에서 찾지 못했습니다 — ${where}이 바뀐 듯합니다. 창을 닫고 다시 열어 주세요.`;
  }
  const head = '이 이미지는 아직 서버에 등록되지 않은 그림이라 편집·AI 요청을 걸 수 없습니다.';
  /**
   * **등록을 먼저 말한다**(2026-09-04 오너 보고). 이 자리에 「빼기」만 두었더니 화면이
   * 「빼는 것 말고는 아무것도 못 하는」 상태가 됐다. 실제로 사람이 원하는 것은 대개 그 반대다 —
   * 이 이미지로 그림을 삼고 거기에 AI 요청·편집을 걸고 싶어서 붙여넣은 것이다.
   */
  if (canRegister) {
    return `${head} [새 그림으로 등록]하면 바로 됩니다 — [저장]해도 등록됩니다.`
      + (canRemove ? ` 잘못 넣었으면 [${where}에서 빼기].` : '');
  }
  return canRemove
    ? `${head} [${where}에서 빼기]로 지울 수 있습니다.`
    : `${head} [편집기] 탭에서 지운 뒤 저장하세요.`;
}

/**
 * 같은 그림이 그 field 에 둘 이상이라 어느 자리인지 **못 가르는** 경우.
 * 서버가 주는 대조값이 `src` 뿐이라(회신 §2 Q5) 원리적으로 갈 수 없다 — 아는 척하지 않는다.
 */
export function describeDuplicateFigure(field) {
  const where = field === 'solution' ? '풀이' : '본문';
  // 줄 전체가 서버와 같은 순서면 자리로 가른다(FigureModal) — 여기 오는 것은 순서까지 어긋난 경우다.
  return `같은 그림이 ${where}에 둘 이상 있고 순서가 서버와 달라, 누른 것이 몇 번째인지 가릴 수 없어 막았습니다`
    + ' — 하나를 지우고 [저장]한 뒤 다시 열어 주세요.';
}

/**
 * 편집은 되는데 **본문에 서버가 모르는 이미지가 섞여 있다**는 사실 고지.
 *
 * 경고가 아니다 — 누른 그림은 바이트로 찾아냈으므로 순번이 정확하고 쓰기가 안전하다(FigureModal
 * 의 `match.bySrc`). 다만 그 상태로 그림을 고치면 **편집본이 폐기된다**(계약 §10 회신 §2 의
 * 대조 조건이 노드 수를 보기 때문). 그 사실을 미리 말해 두는 자리다.
 */
/**
 * 서버에는 있는데 본문에는 없는 그림 — 본문에서 지웠으나 아직 저장하지 않았거나, 지난 저장이
 * 놓친 그림이다(2026-09-04 실측 — 도판을 눌러 둔 채 붙여넣어 도판이 서버에만 남았다).
 * 누른 그림은 바이트로 찾아 쓰기가 열려 있으니 **막지 않고**, [저장]이 맞춰 준다는 사실만 말한다.
 */
export function describeServerExtraNote(count, field) {
  const where = field === 'solution' ? '풀이' : '본문';
  return `서버에 ${where} 그림이 ${count}장 더 있습니다(${where}에서 지운 그림) — [저장]하면 서버에서도 지워집니다.`;
}

export function describeExtraImagesNote(count, field) {
  const where = field === 'solution' ? '풀이' : '본문';
  return `${where}에 아직 등록되지 않은 이미지가 ${count}장 있습니다 — 이 그림은 그대로 고칠 수 있습니다.`
    + ` [저장]하면 그 이미지가 그림으로 등록되어 수가 맞고, 그 전에 고치면 ${where}의 편집본이 버려질 수 있습니다.`;
}

/**
 * 편집본(`lexical_edited`)이 있는 field 에서 대조가 어긋났을 때의 안내. **OCR 검수 전용**이다 —
 * 편집본이라는 개념이 그쪽에만 있고, 아래 문구가 그 화면에만 있는 버튼 이름을 부른다.
 *
 * 회신 §2 Q3 이 문구까지 지정했다 — 「편집본에서 바뀐 그림 — 편집본을 폐기해야 편집 가능」.
 * 왜 일반 문구와 갈라야 하는가: 이때 [저장]도 [다시 읽기]도 답이 아니다. 서버가 세는 그림은
 * 원문(payload) 기준인데 검수자가 편집본에서 그 그림을 지웠거나 바꿔 둔 상태라, **편집본을 버려야**
 * 둘이 다시 맞는다.
 *
 * ── 「폐기하라」만 말하면 막다른 화면이다 ──
 * 회신이 폐기 수단으로 든 넷(PATCH·재요청·되돌리기·삭제)은 **이 안내가 뜬 순간 전부 잠긴 그 넷**이다.
 * 순환이라 사용자가 할 수 있는 일이 없다. 그래서 지정 문구를 그대로 내지 않고, **지금 화면에 실제로
 * 있는 길**을 이름으로 적는다 — 같은 모달의 [이 문제 다시 OCR]이다. 그 확인창이 스스로
 * 「직접 수정한 본문·풀이(편집본)도 초기화됩니다」라고 말하고 있어(OcrReviewModal.openReocrAi),
 * 손실(AI 정답·풀이도 함께 지워짐)도 이미 경고되는 범위 안이다. 없는 버튼 이름을 부르지 않는다.
 *
 * ⚠ 되물음 Q6(FE-20260829-reply-followup.md)의 답으로 `PATCH {body_lexical:null}` 이 열리면,
 *   이 문장을 [편집본 폐기] 전용 단추 안내로 갈아 끼운다 — 그때는 AI 정답·풀이를 잃지 않는다.
 */
export function describeFigureEditedCopy(field) {
  const where = field === 'solution' ? '풀이' : '본문';
  /**
   * 2026-09-04 정정 — 처방을 [이 문제 다시 OCR]에서 **[닫고 다시 열기]** 로 바꿨다.
   *
   * 계약 §10(회신 §2)이 「검수 상세 GET 의 편집본도 image 노드 수가 payload 그림 수와 같으면
   * `src`/`width`/`height` 를 **현재 렌더로 갱신해서** 내려준다」고 했다. 즉 이 띠가 뜨는 자리
   * (수는 맞는데 바이트가 다름)는 **다시 읽는 것만으로 대개 풀린다.** 종전 문구는 그 한 번을
   * 건너뛰고 곧장 재-OCR 을 시켰고, 그것은 AI 정답·풀이까지 지우는 길이다.
   * 재-OCR 은 다시 읽어도 안 풀릴 때의 마지막 수단으로 뒤에 남긴다.
   */
  return `${where}의 그림이 서버가 가진 그림과 달라 편집을 막았습니다 —`
    + ' 잘못된 그림에 저장하지 않기 위해서입니다.'
    + ' 이 창을 닫고 다시 열어 주세요: 서버가 최신 그림으로 맞춰 내려줍니다.'
    + ` 그래도 같은 안내가 나오면 [이 문제 다시 OCR]로 ${where}의 편집본을 초기화해야 합니다`
    + ' — 그때는 AI 가 만든 정답·풀이도 함께 지워집니다.';
}

/**
 * 서버가 대조용 `src` 를 주지 않은 그림(matchFigure 의 `exact:null`)에 붙이는 사실 고지.
 * 회신 §2 Q3 꼬리: 원본 페이지 이미지가 만료된 픽셀 그림은 `src:null` 로 오고 **대조 대상이 아니다**.
 * 화면은 「대조했다」고도 「다르다」고도 말하지 않는다 — 대조하지 못했다는 사실만 말한다.
 */
export const FIGURE_NO_COMPARE_TEXT =
  '서버가 이 그림의 대조용 원본을 주지 않아, 화면의 그림과 바이트로 맞춰 보지 못했습니다.'
  + ' 종류(선 그림/사진)만 보고 진행합니다.';

/**
 * 대조는 했는데 바이트가 다른 그림 — **막지 않는다**(2026-08-30 정정).
 * 한때 이 경우를 쓰기 차단으로 다뤘고 그 판정이 정상 그림까지 잠갔다. 순번이 실제로 어긋났는지는
 * 개수와 종류가 지키므로, 여기서는 「같지 않다」는 사실만 말하고 원인을 추측하지 않는다 —
 * 편집기에서 크기를 바꾼 것인지 서버가 다시 그린 것인지 화면이 가릴 방법이 없다.
 */
/**
 * 보관 원본이 없다고 **서버가 확인해 준** 그림에서, 두 버튼이 왜 잠겼고 대신 무엇을 할 수 있는지.
 *
 * 「…할 수 없습니다」로 끝내면 막다른 안내가 된다 — 그 그림은 [AI 요청]도, [원래 사진으로]도,
 * (이미 선 그림이라) 문제 상세의 [선 그림으로 바꾸기]도 전부 잠긴 상태다. 실제로 남는 길 하나를 이름으로 부른다.
 *
 * 왜 이런 그림이 생기나: 계약 §4-2 는 원본을 **은행 안에서 픽셀 → SVG 로 처음 바꿀 때** 보관한다.
 * OCR 단계에서 이미 도판으로 만들어져 승격된 그림은 그 전환을 은행에서 겪지 않아 보관본이 없다.
 * 승격 시 원본을 함께 넘길 수 있는지는 백엔드에 물어 두었다(FE-20260829-reply-followup.md).
 */
/**
 * 2026-08-31 다시 씀 — **두 군데가 틀린 말이 됐다.**
 *
 * ① 「AI 가 읽을 것이 없습니다」 — 이제 은행 재요청은 **늘 화면의 그림을 실어 보낸다**
 *    (오너 지시 · FigureReconvertModal). 원본이 보관돼 있지 않아도 AI 는 읽을 것이 있다.
 * ② 부르던 버튼([선 그림으로 바꾸기])이 문제 상세에서 사라졌다. 없는 버튼을 이름으로 부르면
 *    안내가 그대로 막다른 길이 된다 — 이 상수가 막으려던 바로 그것이다.
 *
 * 쓰이는 자리는 하나다: [이 원본으로 되돌리기]가 잠긴 이유(figureDropSpecHint).
 * 그래서 「되돌릴 곳이 없다」를 말하고, 남은 길로 **살아 있는 버튼**을 부른다.
 */
export const FIGURE_NO_ORIGINAL_HINT =
  '서버에 이 그림의 원본 사진이 보관돼 있지 않아 되돌릴 곳이 없습니다.'
  + ' 다시 그리려면 [AI 요청]을 쓰세요 — 지금 보고 있는 그림을 그대로 AI 에 보냅니다.';

/**
 * [원래 사진으로]가 잠긴 **이유**. 사유가 둘인데 문구는 하나였다 — 그러면 한쪽에 거짓말을 한다.
 *
 * 2026-08-30 에 픽셀 그림에도 [편집] 버튼을 내면서 이 자리가 처음 드러났다. 명세가 없는
 * 픽셀 그림에서 이 버튼은 잠기는데, 그때 나오던 문구가 「서버에 이 그림의 원본 픽셀이 없어…」였다.
 * 그 그림이 **바로 그 원본**이다. 잠긴 이유를 틀리게 말하면 사용자를 없는 문제를 고치는 데 보낸다.
 *
 * 2026-09-01 정정: 판정을 `item.spec == null` 로 하고 있었는데, 그러면 **트레이스 그림에**
 * 「이미 원래 사진입니다」라고 말한다 — 그것은 사진이 아니라 사진을 따라 그린 SVG 이고, 하필
 * 그 문장 왼쪽 칸에 진짜 원본 사진이 「원본(변환 전 그림)」이라는 이름으로 떠 있다. 한 카드가
 * 「원본은 여기 있다」와 「이게 곧 원본이다」를 동시에 말했다.
 *
 * 그래서 **figureCapabilities.canDropSpec 이 닫는 조건과 글자 하나까지 같은 식을 쓴다.**
 * 두 자리가 다른 것을 보면 「버튼은 열려 있는데 그 옆이 잠긴 이유를 설명하는」 상태가 반드시 생긴다.
 * 여기서 참이 되는 것은 그 한 갈래뿐이다 — 픽셀이면서 버릴 명세도 없는 그림(400 이 확정).
 */
export function figureDropSpecHint(item) {
  if (item?.spec == null && item?.kind !== 'svg') return '이 그림은 이미 원래 사진입니다 — 버릴 좌표·식이 없습니다.';
  return FIGURE_NO_ORIGINAL_HINT;
}

/**
 * **이 창이 이미 그림을 바꾼 뒤**의 안내(2026-08-31).
 *
 * 그때부터 호스트가 넘겨준 편집기 문서는 옛것이라 순번 대조가 성립하지 않는다 —
 * 창이 다시 읽어도 대조를 건너뛴다(FigureModal.wroteRef). 그 사실을 화면이 말해야 한다:
 * 여기서 FIGURE_NO_COMPARE_TEXT(「서버가 대조용 원본을 주지 않아…」)를 내면 **거짓**이다.
 * 원인이 서버가 아니라 방금 내가 한 일이고, 사람이 할 일도 다르다(아무것도 안 해도 된다).
 */
export const FIGURE_AFTER_WRITE_TEXT =
  '이 창에서 그림을 바꿨습니다 — 본문 편집기의 그림은 창을 닫을 때 새로 읽습니다.'
  + ' 그때까지는 화면과 서버를 맞춰 보지 않습니다.';

export const FIGURE_INEXACT_TEXT =
  '서버가 가진 그림과 화면의 그림이 바이트까지 같지는 않습니다.'
  + ' 종류(선 그림/사진)와 순번은 맞아 그대로 진행합니다.';

/**
 * 이번 쓰기가 편집본을 버렸다 — 응답의 `edited_copy_dropped:true`(계약 §10 · 회신 §2).
 *
 * **사후 안내다.** 미리 말할 수 없다: 대조가 될지는 서버만 안다. 그래서 문장이 과거형이고,
 * 「할 수 있는 일」이 아니라 **「무엇이 사라졌는지」**를 말한다 — 이미 일어난 일에 대고
 * 「~하세요」를 붙이면 사람이 되돌릴 방법을 찾다가 없다는 것을 뒤늦게 안다.
 *
 * 원인을 함께 적는 이유: 이 일이 나는 경우가 하나뿐이라(편집본에 서버가 모르는 그림이 섞여 있다)
 * 다음에 같은 일을 피할 수 있다.
 */
export const FIGURE_EDITED_DROPPED_TEXT =
  '이 본문의 편집본이 서버가 세는 그림과 맞지 않아 함께 버려졌습니다 —'
  + ' 편집기에서 고쳤던 문장이 OCR 원문으로 되돌아갑니다.'
  + ' 본문에 직접 넣은 이미지가 있으면 그것이 원인입니다.';

/**
 * 올릴 수 없는 이미지 — `figureApi.parseImageDataUri` 의 판정을 화면 말로.
 *
 * **보내기 전에** 말한다. 서버도 같은 것을 400·413 으로 거절하지만(계약 §10 · 회신 §7),
 * 수 MB 를 올려 보내고 나서 듣는 것과 고르는 순간 듣는 것은 다르다.
 *
 * 크기는 MB 로 적는다 — 바이트나 KB 로 적으면 사람이 「얼마나 줄여야 하는지」를 계산해야 한다.
 */
export function describeImageReject(check) {
  if (!check || check.ok) return null;
  if (check.reason === 'size') {
    const mb = (Number(check.bytes) / (1024 * 1024)).toFixed(1);
    return `이미지가 ${mb}MB 로 너무 큽니다 — 2MB 이하만 올릴 수 있습니다.`
      + ' 캡처 범위를 좁히거나 그림 부분만 잘라 주세요.';
  }
  return 'PNG · JPEG · WEBP 이미지만 올릴 수 있습니다.';
}

/**
 * 삭제 확인창이 말해야 하는 것. 문구를 컴포넌트에 박지 않고 여기 둔 이유는 **검사할 수 있어야**
 * 해서다 — 이 창의 값은 「무엇을 보여 주는가」가 아니라 「사용자에게 무엇을 알리는가」이고,
 * 다음에 창을 다듬을 때 세 줄 중 하나가 조용히 빠지면 화면이 거짓말을 하게 된다.
 *
 * 종전 문구(ImageComponent 의 window.confirm)는 「저장해야 반영되고, 저장 전에는 [편집 취소]로
 * 되돌릴 수 있습니다」였다. 2026-08-29 회신 §2 Q8 로 삭제가 **DELETE API 즉시 반영**이 되면서
 * 그 문장이 통째로 거짓이 됐다 — 그래서 창을 다시 썼다(오너 결정 2026-08-29).
 *
 * @param field 'body' | 'solution'
 * @param lexicalEdited 이 field 에 편집기 편집본(lexical_edited)이 저장돼 있는가
 */
export function figureDeleteSummary(field) {
  return `이 그림을 ${field === 'solution' ? '풀이' : '본문'}에서 지웁니다.`;
}

export function figureDeleteWarnings({ field, lexicalEdited, target, editedAtRisk } = {}) {
  const where = field === 'solution' ? '풀이' : '본문';
  /**
   * 되살릴 길이 있는가 — **문제은행의 본문 그림에만 있다.**
   *
   * 2026-08-30 정정: 한때 모든 경우에 「되돌릴 수 없습니다」라고 했는데 은행에서는 거짓이었다
   * (오너 보고 「지운 그림의 이력을 확인하고 되돌릴 수 없다」). 계약 §4 머리말이 은행 그림 쓰기를
   * 「기존 문제 편집 PATCH 와 같은 규약 — 본문 이력에 버전이 하나 쌓이고 **되돌릴 수 있습니다**」로
   * 정했고, 회신 §2 Q8 이 삭제도 「은행은 본문이력(SOURCE_HUMAN) 규약 그대로」라고 확인했다.
   * 다만 같은 머리말이 **풀이 그림은 본문 이력 대상이 아니라고** 못박았고, OCR 에는 본문 이력이 없다.
   * 그래서 그 둘은 종전 문구가 맞다 — 되살릴 수 없다.
   */
  const revertible = target !== 'ocr' && field !== 'solution';
  const lines = [
    // ⓐ 즉시·이 창의 이력은 닫힘. 「이력이 닫힌다」까지 말하는 이유는 이 창에 [이력]이 함께 있어서다 —
    //    지난 버전이 보이는 창에서 그 말을 빼면 사용자가 이 이력으로 되살릴 수 있다고 읽는다.
    revertible
      ? '[저장]을 누르지 않아도 서버에서 바로 지워집니다. 이 창의 이력은 삭제 기록만 남기고 닫히지만,'
        + ' 문제 상세의 [수정 이력]에서 삭제 직전 버전으로 되돌릴 수 있습니다.'
      : '[저장]을 누르지 않아도 서버에서 바로 지워집니다. 이 그림의 지난 버전(이력)은 삭제 기록만 남기고 닫혀, 되돌릴 수 없습니다.',
  ];
  /**
   * ⓑ 편집본 폐기 — **위험이 있을 때만 말한다.**
   *
   * 2026-09-04 정정(계약 §10 · 회신 §2): 그림 쓰기는 이제 편집본을 폐기하지 않는다. 서버가
   * 같은 순번의 image 노드만 지우고 나머지 문장은 그대로 둔다. 그래서 종전 문구
   * 「함께 폐기되고 서버가 다시 그립니다」는 **정상적인 경우에 거짓**이 됐다 —
   * 편집본이 있다는 이유만으로 겁을 주면 사람이 지우기를 피한다.
   *
   * 폐기가 실제로 일어나는 자리는 **순번 대조가 안 될 때** 하나다(삭제의 조건: 편집본 image
   * 노드 수 == 서버 그림 수 + 1). 검수자가 「본문에 그냥 넣기」로 참고 이미지를 더해 둔 편집본이
   * 그 갈래다. 그 판정은 창이 이미 쥐고 있어(matchFigureSequence) 여기까지 나른다.
   *
   * `editedAtRisk` 를 주지 않으면(모르면) 말하지 않는다 — 이 저장소의 기존 규칙 그대로다
   * (OcrReviewModal 의 재-OCR 경고: 「지울 것이 있을 때만 그 사실을 말한다」).
   */
  if (lexicalEdited === true && editedAtRisk === true) {
    lines.push(`이 ${where}의 편집본이 서버가 세는 그림과 어긋나 있습니다 —`
      + ` 지우면 편집본이 폐기되고 서버가 ${where}을 다시 그립니다(편집기에서 고친 문장이 사라집니다).`);
  }
  // ⓒ 순번 당김. 이 창의 index 가 곧 다른 그림을 가리키게 되므로 창을 닫는 것까지 함께 알린다.
  lines.push(`뒤에 오는 그림들은 순번이 한 칸씩 당겨집니다 — 지운 뒤 이 창은 닫히고 ${where}을 다시 읽습니다.`);
  return lines;
}

/**
 * 원본이 없을 때 그 자리에 적는 말 — **`figureOriginalKeeping` 의 셋과 한 벌**이다.
 *
 * 셋을 한 문구로 뭉개면 반드시 한쪽에 거짓말이 된다. 남은 길이 서로 다르기 때문이다 —
 *   · `gone`      되돌리기도 원본 재생성도 **409 가 확정**이다. 「없다」고 말해야 한다.
 *   · `kept`      화면이 못 그릴 뿐, `use_original:true` 로 서버가 제 보관본을 쓴다(계약 §9-4).
 *                 「없다」고 말하면 되는 길을 포기하게 만든다.
 *   · `unknown`   서버가 말해 주지 않았다. **모르는 것을 아는 척하지 않는다** — 눌러 보면
 *                 서버가 판정한다는 것까지가 화면이 정직하게 할 수 있는 말이다.
 */
const BANK_ORIGINAL_TEXT = {
  gone: '변환 전 원본 사진이 서버에 남아 있지 않습니다 — 원본으로 되돌리거나 원본을 보고 다시 그릴 수 없습니다.',
  kept: '변환 전 원본이 서버에 보관돼 있지만 이 화면으로 받지 못했습니다 — 보여 주지는 못해도 원본으로 다시 그리는 것은 됩니다.',
  unknown: '변환 전 원본을 이 화면으로 받지 못했습니다 — 서버에 남아 있는지는 알 수 없어, [AI 요청]에서 [원본 사진]을 고르면 서버가 판정합니다.',
};

/**
 * OCR 은 원본을 `crop`(지금 페이지에서 bbox 로 자른 것)으로 받는다 — **페이지가 만료되면 null**
 * 인데 보관본은 살아 있을 수 있다(FE-20260829-reply-followup §2 실측: ocr_prob 532 는
 * `crop:null` 인데 `has_original:true`). 그래서 「받지 못함」과 「없음」이 여기서 갈린다.
 *
 * 2026-09-06: 「만료」라고 **단정하지 않는다.** crop 이 null 인 까닭이 둘이다 — 페이지 이미지 만료, 그리고
 * 등록(900)·생성(800)으로 생긴 블록은 애초에 지면 위치(bbox)가 없다(회신 09-04 §1 「bbox 없는 자체 이미지
 * 블록」). 지웠다 다시 등록한 그림에 「페이지가 만료됐다」고 말하면 사람은 엉뚱한 원인을 찾는다(감사 G5).
 * 「다시 그리는 것은 된다」도 뺐다 — 빈 body 재요청의 재료는 자체 이미지 > 페이지 크롭이고(09-04 §4) 보관 blob 으로
 * 물러난다는 말은 계약에 없다. 계약이 보장하는 것은 `spec:null` 되돌리기뿐이라 그것만 말한다.
 * **2026-09-06 회신 B(dev `92c98ef`)로 OCR 항목에도 `original_src` 가 실린다** — `has_original` 이면 data-URI, 아니면 null.
 * 그래서 아래 문구는 보관 원본도 크롭도 픽셀 자체도 없는 그림에만 남는 자리가 됐다.
 */
const OCR_ORIGINAL_TEXT = {
  /**
   * 2026-09-06 다시 씀 — 「받지 못했다」가 **통신이 실패한 것처럼** 읽혔다(오너 「원본이 안 보인다」).
   * 실제로 이 갈래에 오는 그림은 대개 원본이 **처음부터 없는** 것이다: dev 실측으로 원본 없는 그림은
   * 전부 이력 v1 이 800(생성)이거나, OCR 이 크롭을 남기지 않은 옛 프로젝트였다(2026-09-06, 24장 중 8장).
   * 어디서 온 그림인지는 이력이 알고 있으므로 그 길을 이름으로 준다 — 화면이 짐작해 단정하지 않는다.
   */
  gone: 'OCR 당시의 원본이 서버에 없습니다 — AI 가 지어냈거나, 사람이 넣었거나, OCR 이 지면 크롭을 남기지 않은 그림입니다. 어디서 온 그림인지는 [이미지 교체]의 이력에서 볼 수 있습니다.',
  kept: '원본 크롭을 받지 못했습니다(페이지 이미지가 만료됐거나, 등록·생성한 그림이라 지면 위치가 없습니다) — 서버가 보관한 원본으로 되돌리는 것은 됩니다.',
  unknown: '원본 크롭을 받지 못했습니다(페이지 이미지가 만료됐거나, 등록·생성한 그림이라 지면 위치가 없습니다) — 서버에 원본이 남아 있는지는 이 화면에서 알 수 없습니다.',
};

/**
 * OCR 당시 지면에서 bbox 로 잘라낸 사진 — **두 대상이 다른 키로 같은 것을 준다**(계약 §10-2 표 「OCR 은 `crop` 이
 * 같은 뜻」). OCR 은 `crop`(목록 GET 마다 지금 페이지에서 다시 자름 · 만료·bbox 없음이면 null), 은행은 `origin_crop`
 * (승격 출신만 · 목록·이력 GET 에만). 한 이름으로 부르고 한 함수로 읽는다 — 두 화면이 같은 카드를 내려면
 * 값을 읽는 자리부터 하나여야 한다(2026-09-06 오너 지시 「검수 모달과 문제 편집의 과정이 동일해야」).
 */
export function figurePageCrop(item, target) {
  const v = target === 'ocr' ? item?.crop : item?.origin_crop;
  return typeof v === 'string' && v ? v : null;
}

/** 교재 크롭 카드의 이름 — AI 창의 카드는 짧게, 그림 창의 칸 제목은 무엇의 크롭인지까지. 두 대상이 같은 이름을 쓴다. */
export const FIGURE_CROP_LABEL = '교재 크롭';
export const FIGURE_CROP_PANE_LABEL = '교재 크롭(OCR 당시 지면)';
/** 픽셀 그림이 이미 그 크롭 그대로일 때 [교재 크롭으로 바꾸기]를 잠그며 하는 말. */
export const FIGURE_CROP_UNCHANGED_HINT = '이 그림은 이미 교재 크롭 그대로입니다 — 바꿀 것이 없습니다.';

/**
 * 서버가 변환 전 원본을 갖고 있나 — **셋으로 가른다.** 「없다」와 「모른다」는 다른 말이다.
 *
 * `has_original` 키가 **아예 안 왔을 때**를 「없다」로 읽어 버튼을 잠갔다가, 한 번도 변환한 적
 * 없는 픽셀 그림에서 [AI 요청]이 영영 잠긴 적이 있다(2026-08-30 오너 보고 두 번 · figureCapabilities
 * 주석). 모르는 것으로 막으면 서버가 필드를 늦게 붙이거나 이름을 바꾸는 순간 멀쩡한 기능이 죽고,
 * 화면은 그 이유를 「없어서」라고 **거짓으로** 말한다. 그래서 모르면 눌러서 서버가 판정하게 둔다.
 *
 * @returns 'kept'(보관돼 있다) | 'gone'(서버가 없다고 말했다) | 'unknown'(키가 안 왔다)
 */
export function figureOriginalKeeping(item) {
  if (item?.has_original === true) return 'kept';
  if (item?.has_original === false) return 'gone';
  return 'unknown';
}

/**
 * 좌측 「원본」 칸이 무엇을 그리고 자기를 뭐라 부를지 — **값과 이름을 한 자리에서 함께** 정한다.
 *
 * 갈라 두면 반드시 어긋난다. 종전이 그랬다: 은행에서 값은 `item.src`(= 노드에 실린 **지금**
 * 이미지)인데 이름만 「원본」이라 변환된 그림에서 좌·중 두 칸이 같은 SVG 였다. 그래서 이름을
 * 「지금 본문의 그림」으로 물러나 있었고, 이 기능의 존재 이유(회신 §6 「AI 가 원 8개를 7개로
 * 그린다 — 원본과 나란히 보는 화면이 필요하다」)가 성립하지 않았다.
 *
 * 2026-08-29 회신 §2 Q7 로 **`original_src`**(변환 전 픽셀 원본)가 왔다. 단서가 둘이다 —
 *   · `has_original:true` 인 항목에만 실린다
 *   · **목록 GET·history GET 의 item 에만** 실리고 PATCH·재요청 응답의 item 에서는 `null` 이다
 * 그래서 「보관돼 있나(`has_original`)」와 「그 이미지를 실제로 받았나(`original_src`)」는
 * **서로 다른 질문**이다. 버튼 잠금은 계속 앞엣것(figureCapabilities), 이 칸은 뒤엣것으로 가른다.
 *
 * 2026-09-01(계약 §9-4): 은행은 이제 `original_src` 를 **사실상 전부** 준다 — 승격·재승격 때
 * SVG 그림도 지면 크롭 원본을 함께 보관하고, 기존 승격분은 dev 에서 소급 백필됐다. 그래서
 * 아래 폴백(「지금 본문의 그림」)은 예외 경로가 됐다. **그래도 지우지 않는다** — 백필이 닿지
 * 않은 버전이나 다른 배포 환경에서는 여전히 비고, 그때 이름만 「원본」으로 남으면 이 칸이
 * 거짓말을 한다. 이 기능의 존재 이유가 「원본과 나란히 대조」라서 그 거짓은 치명적이다.
 *
 * 2026-09-06 회신 B(dev `92c98ef`): **검수 항목에도 `original_src` 가 실린다** — `has_original` 이면
 * data-URI, 아니면 null 이다. 그래서 두 대상이 같은 순서로 읽는다. OCR 의 `crop`(원본 페이지에서
 * bbox 로 자른 PNG)은 이제 원본 자리가 아니라 **그 다음 칸**(교재 크롭)이다.
 *
 * ── 2026-09-01: 없을 때 **왜 없는지**까지 여기서 정한다 (오너 지시 「원본 이미지가 삭제된
 * 상태면 삭제됐다고 정확히 표출」) ──
 * 종전에는 원본이 없으면 자리를 그냥 비웠고, AI 요청 창은 [원본 사진] 카드를 통째로 뺐다.
 * 빈칸은 고장으로 읽힌다 — 사람은 왜 고를 수 없는지 알 길이 없었다(이 저장소가 이미 같은
 * 이유로 이력 줄에 「가져올 수 없음」을 남긴다 · FigureHistoryList).
 * 그래서 반환에 `state` 와 `text` 를 더했고, 원본을 말하는 세 자리(창 위 칸 · [이미지 교체]의
 * 원본 카드 · AI 요청 창의 [원본 사진])가 **모두 이 판정 하나**를 쓴다.
 *
 * @returns { src, label, state, text, serverHas } — 한 벌이다. 값·이름·없을 때 할 말·
 *   서버 쪽 길이 열려 있는지까지 여기서 함께 정한다. 갈라 두면 반드시 어긋난다.
 *   · `state` — 'ok'(받았다) | 'unavailable'(보관은 됐는데 못 받았다) | 'gone'(서버에 없다) | 'unknown'(서버가 말을 안 했다)
 *   · `text`  — 'ok' 면 빈 문자열. 아니면 **왜 없고 무엇이 남았는지** 한 줄
 *   · `serverHas` — 되돌리기·`use_original` 이 성립할 수 있는가. 'gone' 일 때만 false
 */
export function figureOriginalPane(item, target) {
  const keeping = figureOriginalKeeping(item);
  const gone = keeping === 'gone';

  /**
   * ── 2026-09-06: **두 대상이 한 규칙이다**(오너 지시 「검수 모달과 문제 편집의 과정이 동일해야」) ──
   * 종전에는 OCR 갈래가 따로 있어 `crop` 하나만 보고 이름을 「원본(교재에서 잘라낸 그림)」이라 했다. 그 전제
   * (「OCR 은 크롭이 곧 원본」)는 손대지 않은 bbox 블록에서만 참이다 — 등록(900)·교체(950) 뒤에는 크롭이 계속
   * 오면서도 서버가 보관한 원본은 올린 이미지고, 등록 블록은 크롭이 아예 없는데 자체 이미지가 곧 원본이다(감사 G1·G5).
   * 이제 순서가 같다: 보관 원본(`original_src`, OCR 에도 오면 읽는다) → 교재 크롭(figurePageCrop) → 픽셀 자체 →
   * 지금 그림. 돌려주는 `kind` 로 호출부가 「이 칸이 무엇을 그리고 있나」를 안다 — 크롭이면 [교재 크롭으로 바꾸기]
   * (`PATCH {image}`), 보관 원본이면 [이 사진으로 바꾸기](`spec:null`)다.
   */
  // 보관 원본 — 2026-09-06 회신 B 로 **두 대상 모두** 실려 온다(종전에는 은행만).
  const original = item?.original_src || null;
  if (original) {
    return { src: original, label: '원본(변환 전 그림)', state: 'ok', serverHas: !gone, text: '', kind: 'stored' };
  }
  const crop = figurePageCrop(item, target);
  if (crop) {
    return { src: crop, label: FIGURE_CROP_PANE_LABEL, state: 'ok', serverHas: !gone, text: '', kind: 'crop' };
  }
  // 픽셀 그림은 노드의 src 가 **곧 원본**이다(계약 §1-2 · 등록·교체 블록도 같다) — 그때만 「원본」이라 불러도 참이다.
  if (item?.kind === 'pixel' && item?.src) {
    return { src: item.src, label: '원본', state: 'ok', serverHas: !gone, text: '', kind: 'pixel' };
  }
  // 보관 원본을 받지 못했다 — 남은 것은 노드에 실린 **지금** 이미지뿐이라 그 이름으로 부른다.
  // 없는 까닭은 대상마다 다르게 말한다(OCR 은 크롭을 못 받은 것, 은행은 보관 원본을 못 받은 것).
  return {
    src: item?.src || null,
    label: item?.kind === 'svg' ? '지금 본문의 그림' : '원본',
    state: gone ? 'gone' : keeping === 'kept' ? 'unavailable' : 'unknown',
    serverHas: !gone,
    text: (target === 'ocr' ? OCR_ORIGINAL_TEXT : BANK_ORIGINAL_TEXT)[keeping],
    kind: 'current',
  };
}

/**
 * 그림 창 맨 왼쪽 **「원본」 칸**의 머리줄 이름과, 사진 대신 적을 말.
 *
 * ── 2026-09-06 오너 지시 「OCR 당시의 원본은 표출하는 것부터 우선 작업해줘(지금 안 보임)」 ──
 * 그전까지 원본은 [이미지 교체]를 **펴야** 나오는 80px 썸네일 하나뿐이었다. 창을 열면 좌표·식 폼만
 * 보이고, 원본이 있는지조차 알 수 없었다(오너가 「안 보인다」고 한 자리가 여기다). 칸을 늘 세우면
 * 「원본이 있나」에 화면이 **늘** 답한다 — 그래서 없을 때 할 말이 필요하고, 그 말이 이 함수다.
 *
 * 2026-09-01 의 「이미지는 두 개가 아니라 하나만」과 어긋나지 않는다. 그 지시는 **미리보기 칸이 둘로
 * 갈려 「지금 저장하면 무엇이 되는가」가 흐려진** 것을 막은 것이다. 미리보기는 그대로 한 칸이고,
 * 이 칸은 고치는 자리가 아니라 **대조하는 자리**다 — 답하는 물음이 다르므로 두 칸이 다투지 않는다.
 *
 * 픽셀 그림을 따로 가르는 이유: 그 그림은 자체가 원본이라(계약 §1-2) 사진을 또 그리면 같은 그림이
 * 두 칸에 서고, 「원본 없음」이라 적으면 거짓이 된다.
 */
export function figureOriginPaneLabel(pane) {
  if (!pane) return '원본';
  if (pane.kind === 'stored' || pane.kind === 'crop') return pane.label;
  if (pane.kind === 'pixel') return '원본';
  return '원본 없음';
}

export function figureOriginPaneNote(pane) {
  if (!pane) return '';
  if (pane.kind === 'stored' || pane.kind === 'crop') return ''; // 사진이 선다 — 할 말이 없다
  if (pane.kind === 'pixel') return '이 그림 자체가 원본 사진입니다 — 좌표·식으로 옮긴 적이 없습니다.';
  return pane.text || '서버에 보관된 원본이 없습니다.';
}

/**
 * [AI 요청] 확인창의 설명. **두 문장이고, 각각 답하는 물음이 정해져 있다.**
 *   ① 무슨 일이 일어나나 — AI 가 이 그림을 다시 그린다.
 *   ② 내가 할 일이 있나 — 원본을 올려야 하나, 안 올려도 되나.
 *   ③ 잘못되면 어쩌나 — 지금 그림은 이력에 남고, 되돌리는 버튼을 이름으로 부른다.
 *
 * ── 2026-08-31 다시 씀 (오너 지시 「사람이 사용하는 명료한 문장으로」) ──
 * 종전 문장은 두 군데가 잘못돼 있었다.
 *
 * ① **틀린 말이 됐다.** 머리글이 「좌표·식으로 된 선 그림으로 만듭니다」였는데, 계약 §9-4 로
 *    변환 모드가 셋이 되면서(있는 그대로 옮기기·사진 따라 그리기·문제 읽고 새로 그리기)
 *    그 설명은 **첫째 것 하나만** 가리킨다. 「사진 따라 그리기」는 윤곽을 그대로 뜨는 것이라
 *    좌표도 식도 아니다. 어떻게 옮길지는 바로 아래 고르는 칸이 말하므로 여기서 말하지 않는다.
 * ② **서버 속사정을 사람에게 읊고 있었다.** 「본문에 실린 원본 이미지, 없으면 이력에 보관해 둔
 *    변환 전 그림입니다」는 계약 §4-3 의 **탐색 우선순위**이고, 읽는 사람이 할 일을 하나도
 *    바꾸지 않는다. 그가 알아야 하는 것은 「올릴 게 없다」 한마디다.
 *
 * 대상마다 갈리는 것은 ②뿐이다 — OCR 은 서버가 교재 페이지를 잘라 쓰고(계약 §3-3),
 * 은행은 **화면이 보내는 그림**을 쓴다(§4-3 의 1순위 `body.image`). 「교재 페이지를 다시 읽어
 * 온다」고 오해하면 결과가 기대와 다를 때 원인을 엉뚱한 데서 찾으므로, 그 한 조각은 남긴다.
 *
 * ── 2026-08-31 두 번째 고침 (오너 지시 「요청할 때는 현재 보고 있는 이미지를 보내라」) ──
 * 종전에는 은행 문구가 둘로 갈렸다 — 보관 원본이 **없다고 확인된** 그림에만 「화면의 그림을
 * 보낸다」고 말하고, 나머지에는 「서버가 알아서 찾는다」고 했다. 이제 은행은 **늘 보낸다**.
 * 그래서 갈래가 사라졌고 `needsImage` 인자도 함께 없앴다 — 값이 무엇이든 같은 말을 하는
 * 인자는 다음 사람에게 「여기서 무언가 갈린다」는 거짓 신호가 된다.
 *
 * ── 2026-09-01 세 번째 고침 — **두 문장이 다 거짓이 돼 있었다** ──
 * ① 「지금 보고 있는 그림을 그대로 AI 에 보냅니다」 — 같은 날 기본 재료가 [원본 사진]으로
 *    뒤집혔다(FigureReconvertModal 의 defaultPick). 창은 맨 위에서 「보고 있는 것을 보낸다」고
 *    하고 조금 아래에서 「기본은 [원본 사진]입니다」라고 해, **한 창이 두 말을** 했다.
 * ② 「[이미지 교체]로 되돌릴 수 있습니다」 — 사진을 따라 그린 판(`spec:null`)은 이력에서
 *    되살릴 수 없다. 이력이 버전별 그림을 주지 않아(계약 §3-4) 좌표·식 버전만 가져올 수 있고,
 *    사진으로 돌아가는 곳은 보관 원본 **한 장**뿐이다(§4-2). 없는 안전망을 약속하면 사람은
 *    마음 놓고 누른다 — 오너가 하루에 재요청 여덟 번을 누른 이력이 그 결과다.
 *
 * ── 2026-09-01 네 번째 고침 — **②의 근거가 그날 안에 무너졌다** ──
 * 같은 날 백엔드가 「판 내용 스냅샷」을 넣었다(계약 §9-4, dev `427a52c`): 이제 모든 판이 그
 * 시점의 그림을 저장하고, 트레이스 판을 고르면 **그 판의 SVG(글자 수정 포함)로 정확히 복원**된다.
 * 그래서 「되살릴 수 없습니다」는 이제 **거짓일 수 있다** — 겁을 주어 못 누르게 하는 쪽으로 틀린다.
 *
 * 그렇다고 「되살릴 수 있습니다」로 뒤집지도 않는다. 도입(09-01) 전에 만들어진 판은 내용이
 * 소실돼 여전히 못 되살린다(`has_content:false`). 판마다 갈리는 값을 이 함수는 모른다 —
 * **한 판씩 답이 다른 물음에 창이 하나로 단정하지 않고, 확인할 자리를 이름으로 부른다.**
 * 판별 표시는 그 자리(이력 목록)가 이미 판마다 하고 있다(figureVersionRow 의 hasContent·lostContent).
 *
 * @param traced 지금 그림이 「사진 따라 그리기」로 만든 것인가(figureTrace 가 판정).
 * @param defaultsToOriginal 창이 실제로 [원본 사진]을 골라 두는가. **창이 알고 이 함수는 모른다** —
 *   기본은 「보관 원본을 실제로 받았을 때만」 원본이고(FigureReconvertModal 의 defaultPick),
 *   `has_original` 만 참이고 그림을 못 받은 그림에서는 [지금 그림]이 골라진다. 그 갈래에서
 *   무조건 「기본은 원본」이라 말하면, 화면은 원본을 보냈다고 믿게 해 놓고 실제로는 직전 결과를
 *   다시 옮긴다 — 이 문구가 없애려던 바로 그 문제로 조용히 되돌아간다.
 */
export function figureReconvertSummary(target, { traced = false, defaultsToOriginal = true, defaultKey = null } = {}) {
  if (FIGURE_SVG_EDITOR) return '그림 재료를 고르고 필요한 수정을 적어 주세요. AI가 그린 결과는 새 판으로 저장됩니다.';
  // 무엇을 재료로 보낼지 **사람이 고른다** — 2026-09-05 부터 OCR 도 같은 카드를 낸다(회신 §4 로
  // OCR 재요청이 image 를 받고, 아무것도 안 보내면 서버 보관 원본을 쓴다). 기본값을 여기서 말해
  // 두어야 아래 카드 칸의 안내와 한 말이 된다 — 두 자리가 다른 말을 하면 사람은 무엇이 나가는지 알 수 없다.
  // 2026-09-06: 기본이 셋이 됐다(원본 사진 · 교재 크롭 · 지금 그림) — 창이 `defaultKey` 로 넘긴다. 옛 인자도 받는다.
  const key = defaultKey ?? (defaultsToOriginal ? 'original' : 'screen');
  const source = key === 'original'
    ? `무엇을 보고 다시 그릴지는 아래에서 고릅니다 — 기본은 서버가 보관한 [${figureOriginalCardLabel(target)}]입니다.`
    : key === 'crop'
      ? `무엇을 보고 다시 그릴지는 아래에서 고릅니다 — 기본은 OCR 당시 지면에서 잘라낸 [${FIGURE_CROP_LABEL}]입니다.`
      : '무엇을 보고 다시 그릴지는 아래에서 고릅니다 — 기본은 [지금 그림]입니다.';
  // 되돌리는 길을 **이름으로 부른다** — 이 저장소가 막다른 안내를 막는 방식이다
  // (FIGURE_NO_ORIGINAL_HINT·FIGURE_SUGGEST_TRACE_ELSEWHERE_TEXT 와 같은 규칙).
  // 서식 없는 문자열로 나간다(창이 그대로 그린다) — 별표를 쓰면 글자로 보인다.
  const back = traced
    // **단정하지 않는다**(2026-09-01 네 번째 고침 — 위 머리주석 ④). 되살아나는 판과 그렇지
    // 않은 판이 섞여 있고, 어느 쪽인지는 판마다 다르다. 여기서 「된다」·「안 된다」 중 하나를
    // 고르면 반은 거짓이 된다 — 답을 아는 자리로 보낸다.
    ? ' 지금 그림은 사진을 따라 그린 것입니다 —'
      + ' 되돌릴 수 있는 판이 남아 있는지는 [이미지 교체] 목록에서 확인하세요.'
    : ' 지금 그림은 이력에 남으니, 결과가 마음에 들지 않으면 [이미지 교체]로 되돌릴 수 있습니다.';
  return `이 그림을 AI 가 다시 그립니다. ${source}${back}`;
}

/**
 * 트레이스 그림에서 [AI 요청] 창이 **맨 먼저** 하는 말 (2026-09-01).
 *
 * 오너 보고 「AI 요청할 때 이미지가 잘 수정되지 않고, 프롬프트도 전달되지 않는 것 같다」의
 * 정체가 이것이다. 프롬프트는 전달된다 — 서버 이력의 `user_prompt` 에 여덟 번이 그대로 남아
 * 있다(「숫자 틀렸음」·「명수를 6명으로 바꿔줘」·「5명을 6명으로 고치고 나머진 그대로」…).
 * 다만 재요청은 **보관 원본 사진을 다시 훑는 일**이라, 사진에 인쇄된 글자가 매번 그대로 돌아온다.
 * 지시는 `alt` 에만 물렸다(실측: alt 는 「…6명…」인데 그려진 글자는 「5명」이었다).
 *
 * 글자를 바꾸는 정본 경로는 따로 있다 — `PATCH {trace_texts}`(계약 §9-4). 그 길이 화면에
 * 있는데도 이 창에서 말하지 않으면, 사람은 여기 있는 것으로 그 일을 하려 하고 영영 안 된다.
 */
export const FIGURE_TRACE_RECONVERT_WARN =
  '그림 속 글자만 고칠 거라면 이 창이 아닙니다 — 창을 닫고 [그림 속 글자] 칸에서 고치세요.'
  // 뒷문장에 **조건을 적는다.** 되돌아가는 것은 원본 사진을 재료로 보낼 때이고, 그것이 기본값이라
  // 창을 여는 순간에는 참이다. 다른 카드를 고른 사람에게까지 단언하면 그때는 거짓이 된다.
  + ' [원본 사진]을 재료로 다시 그리면 사진에 인쇄된 글자로 되돌아갑니다.';

/**
 * 트레이스 그림의 고치는 칸에 적는 말 (2026-09-01).
 *
 * 그 그림은 `kind:'svg'` 인데 `spec:null` 이라 고칠 좌표·식이 없다. 그런데 폼은 빈 채로 열려
 * 있었고, 잠긴 [저장] 아래 안내는 「그림 설명(한 문장)을 채워야 저장할 수 있습니다」였다 —
 * 그대로 따라가면 `PATCH {spec}` 이 나가 **127KB 짜리 트레이스가 선 몇 개짜리 도형으로 갈린다.**
 * 화면이 사람을 그 길로 안내하고 있었다. 할 수 있는 일을 이름으로 부르는 문장으로 바꾼다.
 *
 * ── 자리가 하나로 정해졌다(2026-09-01 두 번째, 오너 지시 「합쳐 줄래」) ──
 * 종전에는 「숫자·식」 칸이 이 문장만 띄우고, 「그림 속 글자」는 **다른 상자**로 그 아래 따로
 * 섰다. 한 화면에 칸이 둘인데 하나는 「여기엔 없다」만 말하는 꼴이라, 할 수 있는 일을 찾으려면
 * 사람이 두 자리를 오갔다. 이제 두 칸이 한 칸이고 이 문장은 **그 칸 안의 각주**다 —
 * 그래서 「[그림 속 글자] 칸으로 가라」는 안내가 필요 없어졌다(가리킬 곳이 곧 여기다).
 */
export const FIGURE_TRACE_NO_SPEC_TEXT =
  '이 그림은 사진을 따라 그린 것이라 고칠 좌표·식이 없습니다 — 여기서는 그림 속 글자만 바꿉니다.'
  // 방향어(「아래」)는 여전히 쓰지 않는다. 자리를 가리키는 말은 그 자리를 아는 쪽이 쓴다.
  + ' 좌표·식 그림으로 새로 그리려면 [AI 요청]에서 [있는 그대로 옮기기]를 고르세요.';

/**
 * [AI 생성] 확인창의 문구(계약 §9-2). 셋을 반드시 말한다 —
 * ① **더한다**(있던 그림을 바꾸지 않는다) ② 붙는 자리가 **맨 끝**이다 ③ 수십 초 걸린다.
 *
 * ①②를 빼면 「그림을 새로 그려 준다」로 읽혀, 잘못 그려진 그림을 고치려고 눌렀다가
 * 그림이 둘이 된다. 그때 되돌리는 길은 삭제뿐이고 그것은 이력에 삭제 버전을 남긴다.
 */
export function figureGenerateSummary(field, { withOrigins = false } = {}) {
  const where = field === 'solution' ? '풀이' : '본문';
  // 서식 없는 문자열로 나간다 — 별표를 쓰면 글자로 보인다.
  // 2026-09-05: 생성 창이 재요청 창과 같은 창이 되어 재료(본문 글 / 올린 이미지)를 고른다 — 그 사실을 말한다.
  // 2026-09-06: 이 자리 그림들의 **원본** 카드가 있으면(figureOrigins) 그것을 고른 결과는 「추가」가 아니라
  //   **그 그림을 그 자리에서 다시 그리기**다 — ①과 어긋나는 갈래라 카드가 있을 때만 그 한 줄을 더 말한다.
  return `${where}의 글을 AI 가 읽고 그림 하나를 만들어 ${where} 맨 끝에 붙입니다(있던 그림은 그대로). `
    + '이미지를 올리면 그 이미지를 옮겨 새 그림으로 만듭니다.'
    + (withOrigins ? ' 있는 그림의 원본을 고르면 새로 더하지 않고 그 그림을 그 자리에서 다시 그립니다.' : '');
}

/**
 * 생성 창에서 **있는 그림의 원본** 카드를 골랐을 때, 카드 아래 한 줄(2026-09-06 오너 결정 A 안).
 *
 * 이 창의 머리말은 「맨 끝에 붙인다·있던 그림은 그대로」라고 말한다. 이 카드만 그 말과 다르게 움직이므로
 * (그 그림을 **그 자리에서** 다시 그린다 — 재요청과 같은 일), 고른 순간 그 차이를 그 자리에서 말한다.
 * 말하지 않으면 사람은 [만들기]를 누르고 새 그림을 기다리다가, 있던 그림이 바뀐 것을 보고 고장으로 읽는다.
 *
 * @param index 서버 순번(0부터) — 사람에게는 1부터 센 자리로 말한다.
 */
export function figureRedrawInPlaceText(index) {
  return `${Number(index) + 1}번째 그림을 그 자리에서 다시 그립니다 — 새 그림을 더하지 않고, 지금 그림은 이력에 남아 되돌릴 수 있습니다.`;
}

/**
 * 생성 창에서 있는 그림의 원본으로 다시 그린 뒤의 토스트 — 두 화면(검수·문제)이 같은 말을 한다.
 * 「무엇을 보고 어떻게」를 적는다: 응답에는 그 둘이 없어(계약 §9-4) 화면이 말해야 한다(FigureModal.doReconvert 와 같은 근거).
 */
export function figureRedrawnText(where, index, mode) {
  if (FIGURE_SVG_EDITOR) return `${where} ${Number(index) + 1}번째 그림을 다시 그렸습니다. 그림을 눌러 편집할 수 있습니다.`;
  const how = FIGURE_MODE_TEXT[mode]?.name || FIGURE_MODE_TEXT.faithful.name;
  return `${where} ${Number(index) + 1}번째 그림을 원본을 보고 「${how}」로 다시 그렸습니다 — 그림을 눌러 고칠 수 있습니다.`;
}

/**
 * 같은 자리에서 서버가 「변환 대상 아님」(200 `converted:false`)으로 돌아왔을 때. 실패가 아니다 — 원본은 그대로다.
 * `suggest:"trace"` 가 실려 오면 남은 길을 이름으로 부른다(FIGURE_SUGGEST_TRACE_TEXT 와 같은 규칙).
 */
export function figureRedrawRejectedText(where, index, suggestTrace = false) {
  if (FIGURE_SVG_EDITOR) return `${where} ${Number(index) + 1}번째 그림을 다시 그리지 못했습니다. 기존 그림은 그대로입니다.`;
  return `${where} ${Number(index) + 1}번째 그림은 좌표·식으로 옮길 수 없어 원본을 그대로 두었습니다(손실 없음).`
    + (suggestTrace ? ` ${FIGURE_SUGGEST_TRACE_TEXT}` : '');
}

/**
 * 재요청·생성 창의 [원본] 카드 이름 — 대상마다 원본이 다른 것이다.
 *  · 문제은행: 변환 전 **사진**(계약 §1-2 `original_src`) → 「원본 사진」
 *  · OCR 검수: 서버가 보관한 원본 — 페이지에서 잘라 낸 그림이거나 등록·교체로 올린 이미지(회신 §4) → 「원본(서버 보관)」
 * 이 카드를 고르면 은행은 `use_original:true` 를, OCR 은 **아무것도** 싣지 않는다(서버 기본이 곧 그 원본이다).
 */
export function figureOriginalCardLabel() {
  // 2026-09-06: 두 대상이 같은 이름을 쓴다(오너 지시 「과정이 동일해야」). OCR 의 미리보기 없음은 카드 자리글이 말한다.
  return '원본 사진';
}

/**
 * AI 요청 창의 카드 아래 한 줄 — **무엇이 기본으로 나가는지**를 고른 카드 이름으로 말한다(2026-09-06 한 함수로 모음).
 * 종전에는 「기본은 원본」·「기본은 지금 그림」 두 문장이 창 안에 박혀 있었고, 교재 크롭이 기본이 되는 갈래가 없었다.
 *
 * @param defaultKey 창이 기본으로 골라 두는 카드 — 'original' | 'crop' | 'screen' | 그 밖(할 말 없음)
 * @param canUseOriginal [원본 사진] 카드가 서 있는가 — 'screen' 이 기본인데 카드는 있는 경우와, OCR 에서 크롭이
 *   기본인데 원본 카드도 함께 선 경우(둘의 관계를 말한다)에 쓴다.
 */
export function figureMaterialDefaultNote(defaultKey, { target = 'bank', canUseOriginal = false } = {}) {
  const tail = ' [지금 그림]은 이미 한 번 옮겨진 도판이라 여러 번 요청해도 결과가 잘 달라지지 않습니다.';
  if (defaultKey === 'original') {
    return `기본은 [${figureOriginalCardLabel(target)}]입니다 — 서버가 보관한 변환 전 원본을 보고 다시 그립니다.${tail}`;
  }
  if (defaultKey === 'crop') {
    // 2026-09-06 회신 B 이후로 검수도 `original_src` 를 받으므로 이 갈래는 **원본 키가 비어 온 판**
    // (백필 전 데이터·다른 배포)에서만 선다. 그때 두 카드가 같은 그림일 수 있음을 말해 둔다.
    const both = target === 'ocr' && canUseOriginal
      ? ` [${figureOriginalCardLabel(target)}]은 서버가 보관한 원본이며, 이미지를 등록·교체한 적이 없으면 교재 크롭과 같은 그림입니다.`
      : '';
    return `기본은 [${FIGURE_CROP_LABEL}]입니다 — OCR 당시 지면에서 잘라낸 사진을 보고 다시 그립니다.${tail}${both}`;
  }
  if (defaultKey === 'screen' && canUseOriginal) {
    return `기본은 [지금 그림]입니다 — 서버가 원본을 보관하고 있다고 했지만 이 화면이 그림을 받지 못했습니다. [${figureOriginalCardLabel(target)}]을 고르면 서버가 제 보관본을 그대로 보고 다시 그립니다.`;
  }
  return '';
}

/**
 * 변환 모드의 **화면 이름과 한 줄 설명** — 계약 §9-4(2026-08-31).
 *
 * 값(faithful·trace·schematic)은 서버 것이고, 여기 있는 것은 사람에게 보일 말이다.
 * 이름을 「충실 변환」·「트레이스」·「도식화」로 두지 않은 이유는 이 저장소의 응답 규약과 같다 —
 * **눈으로 짚을 수 있는 말**이어야 한다. 「트레이스」는 무엇을 하는지 그림이 안 그려진다.
 *
 * · faithful  「있는 그대로 옮기기」 — 그래프·도형을 좌표와 식으로 옮긴다. 기본값.
 * · trace     「사진 따라 그리기」 — 윤곽을 그대로 따라 그리고 그림 속 글자를 얹는다.
 * · schematic 「문제 읽고 새로 그리기」 — 삽화를 버리고 문제 글을 읽어 풀이용 그림으로 짓는다.
 */
export const FIGURE_MODE_TEXT = {
  faithful: {
    name: '있는 그대로 옮기기',
    hint: '그래프·도형을 좌표와 식으로 옮깁니다. 사진·삽화면 옮기지 못하고 원본을 그대로 둡니다.',
  },
  trace: {
    name: '사진 따라 그리기',
    hint: '사진의 윤곽을 그대로 따라 그리고, 그림 속 인쇄 문구는 고칠 수 있는 글자로 얹습니다.',
  },
  schematic: {
    name: '문제 읽고 새로 그리기',
    hint: '삽화 대신 문제의 글을 읽고 풀이에 쓸 그림(원·반지름·높이 같은 것)으로 새로 짓습니다.',
  },
};

/**
 * 이 자리에서 고를 수 있는 모드 — **자리마다 다르다.**
 *
 * 계약 §9-4 가 「변환 모드는 `figspec-convert`·재요청 공통」이라 해 놓고, 바로 아래에서
 * 트레이스만 **「은행 재요청만, dev 배포」** 라고 좁혔다. 그 좁힌 쪽을 따른다 —
 * 눌리면 반드시 실패하는 버튼을 내지 않는다는 이 저장소의 규칙(figureCapabilities)이고,
 * 넓게 열었다가 틀리면 사용자는 「사진 따라 그리기가 고장났다」로 읽는다.
 *
 * **2026-09-05 저녁 — OCR 도 트레이스를 받는다**(계약 §10-2 G3 · BE-20260905-reply-figure-parity.md §3).
 *   같은 날 낮의 dev 실호출은 400 이었고(「OCR 검수 단계에서는 trace 를 지원하지 않습니다」), 그 몇 시간 뒤
 *   백엔드가 열었다. 이제 재요청은 두 대상 모두 셋이다. 생성만 재료가 「본문 글」일 때 둘이다 —
 *   따라 그릴 사진이 없어 서버도 400 을 낸다(§5). 올린 이미지를 골랐으면(등록 뒤 재요청) 셋이다.
 *
 * **2026-09-06 — 생성도 셋을 다 낸다**(오너 지시 「검수 모달에 그림 생성 3가지 방식 추가」). 종전에는 재료가
 *   「본문 글」이면 [사진 따라 그리기]를 숨겼는데(서버가 trace 400), 숨기면 사람은 방식이 둘인 줄 안다.
 *   이제 셋을 다 보이고, 사진 따라 그리기를 골랐는데 이미지가 없으면 **[만들기]를 잠그고 그 자리에서 올리라고
 *   말한다**(figureModeNote) — 방식을 먼저 고르고 재료를 채우는 순서가 사람의 순서다. 올린 이미지는 등록 뒤
 *   재요청으로 가므로(호스트 runFigGen) 서버 쪽 제약과도 어긋나지 않는다.
 *   같은 날 오너 지적 「원본 이미지는 OCR 하면서 생성된 원본을 활용하거나 업로드한 이미지로」 → A 안: 생성 창에
 *   **이 자리 그림들의 원본 카드**를 내고(figureOrigins), 고르면 그 그림을 그 자리에서 다시 그린다(재요청).
 *   그때도 사진이 있는 것이므로 [사진 따라 그리기]가 열린다(FigureReconvertModal.hasPhoto).
 *
 * @param where 'bank-reconvert'(문제은행 그림 AI 요청) | 'ocr-reconvert' | 'generate'(그림 AI 생성 — 두 화면 공통)
 */
export function figureModeChoices() {
  return ['faithful', 'trace', 'schematic'];
}

/**
 * 모드 칸 아래 한 줄 — 고른 방식이 **지금 재료로는 안 될 때** 무엇을 하면 되는지 그 자리에서 말한다.
 * 할 말이 없으면 빈 문자열이다.
 *  · 생성에서 [사진 따라 그리기]를 골랐는데 올린 이미지가 없다 → 이미지를 올리라고 말한다(서버 생성 API 는
 *    trace 를 받지 않는다 — 올리면 등록 뒤 재요청으로 간다).
 */
export function figureModeNote(where, { withImage = false, value = null, withFigures = false } = {}) {
  if (where === 'generate' && value === 'trace' && !withImage) {
    // 2026-09-06(A 안): 이 자리 그림의 원본 카드가 있으면 그것도 사진이다 — 올리기만 길인 것처럼 말하지 않는다.
    return withFigures
      ? '「사진 따라 그리기」는 사진이 있어야 합니다 — 아래에서 그림 원본을 고르거나, [이미지 올리기]나 붙여넣기로 이미지를 넣어 주세요.'
      : '「사진 따라 그리기」는 올린 이미지로 그립니다 — 아래 [이미지 올리기]나 붙여넣기로 이미지를 넣어 주세요.';
  }
  return '';
}

/**
 * 생성 창의 확인 버튼이 [사진 따라 그리기] 때문에 잠겼을 때의 툴팁 — 위 figureModeNote 와 같은 갈래로 말한다.
 */
export function figureTraceLockTitle(withFigures = false) {
  return withFigures
    ? '「사진 따라 그리기」는 그림 원본을 고르거나 이미지를 올려야 합니다'
    : '「사진 따라 그리기」는 이미지를 올려야 만들 수 있습니다';
}

/**
 * 모드 칸의 머리말 — 옮기는 자리(재요청·변환)는 「어떻게 옮길까요」, 짓는 자리(생성)는 「어떻게 그릴까요」.
 * 생성에는 옮길 원본이 없어 「옮기기」가 거짓이 된다.
 */
export function figureModeLegend(where) {
  return where === 'generate' ? '어떻게 그릴까요' : '어떻게 옮길까요';
}

/**
 * 모드 한 줄 설명 — 이름은 자리마다 같고(사람이 같은 것으로 알아본다), 설명만 자리에 맞춘다.
 * 생성은 「본문의 글」이 재료라 「삽화·사진」을 말하는 기본 설명이 맞지 않는다.
 */
export function figureModeHint(mode, where) {
  if (where === 'generate') {
    if (mode === 'faithful') return '본문이 말하는 그래프·도형을 좌표와 식으로 그립니다.';
    if (mode === 'trace') return '사진의 윤곽을 그대로 따라 그리고, 그 안의 글자는 고칠 수 있는 글자로 얹습니다 — 그림 원본이나 올린 이미지가 있어야 합니다.';
    if (mode === 'schematic') return '문제의 글을 읽고 풀이에 쓸 그림(원·반지름·높이 같은 것)으로 짓습니다.';
  }
  return FIGURE_MODE_TEXT[mode]?.hint ?? '';
}

/**
 * 「있는 그대로 옮기기」가 거절하며 `suggest:"trace"` 를 함께 준 경우의 안내(계약 §9-4).
 *
 * 이 한 줄이 없으면 그 응답은 막다른 길로 읽힌다 — 「못 옮깁니다」로 끝나기 때문이다.
 * 서버가 `suggest` 를 실어 보내는 이유가 **남은 길이 있다**는 것이고, 화면은 그 길을 이름으로 부른다.
 */
export const FIGURE_SUGGEST_TRACE_TEXT =
  '좌표·식으로는 옮길 수 없는 그림입니다 — 대신 [사진 따라 그리기]로 윤곽을 그대로 옮길 수 있습니다.';

/**
 * 같은 제안을 **그 모드가 없는 자리**에서 받았을 때(본문의 [선 그림으로 바꾸기]).
 *
 * 계약 §9-4 는 트레이스를 「은행 **재요청**만」으로 좁혔다. 그래서 이 자리에서는 서버가
 * `suggest:"trace"` 를 줘도 여기서 바로 갈 길이 없다 — 대신 **어디로 가면 되는지**를 말한다.
 * 「할 수 없습니다」로 끝내면 실제로는 있는 길이 없는 것처럼 보인다(FIGURE_NO_ORIGINAL_HINT 와 같은 규칙).
 */
export const FIGURE_SUGGEST_TRACE_ELSEWHERE_TEXT =
  '윤곽을 그대로 옮기는 길은 있습니다 — 본문에서 그 그림을 누르고 [AI 요청] → [사진 따라 그리기]를 고르세요.';

/**
 * 서버가 바로잡은 곳 목록의 **머리말**(계약 §9-4 `corrections[]`).
 *
 * 「교정」이라 부르지 않는다 — 무엇을 누가 고쳤는지가 안 보인다. 사람이 알아야 하는 것은
 * **AI 가 잰 값이 그대로 쓰이지 않았다**는 사실이다. 그래야 원본과 대조할 때 어디를 볼지 안다.
 */
export const FIGURE_CORRECTIONS_LABEL = 'AI 가 잰 것을 서버가 바로잡은 곳';

/**
 * **손으로 옮기기가 열리지 않는 까닭** — 판정은 figCoords.figDragState 가 하고, 여기는 그 말을 짓는다.
 *
 * 이 문구가 서는 자리가 2026-09-06 에 바뀌었다(오너 지시). 종전에는 그림 **위에 겹쳐** 떴는데,
 * 도판 아래쪽을 가려 정작 봐야 할 그림이 안 보였다. 이제 미리보기 칸의 **머리줄**이 이 말을 한다 —
 * 그 자리에 있던 「값을 고치면 바로 바뀝니다」는 걷었다(고치면 바뀌는 것은 눌러 보면 아는 일이고,
 * 왜 손잡이가 없는지는 말하지 않으면 알 길이 없다).
 *
 * @param state figDragState 의 반환값. null 이거나 열려 있으면 빈 문자열이다(할 말이 없다).
 */
export function figDragNote(state) {
  const reason = state?.reason || null;
  if (reason === 'no-range') return '가로·세로 범위가 있어야 손으로 옮길 수 있습니다.';
  if (reason === 'mismatch') {
    // 어긋난 양을 적는다 — 「조금」과 「300px」은 사람이 할 일이 다르다(전자는 눈금, 후자는 서버 조판 변경).
    return `그림의 좌표를 읽지 못해 손으로 옮기기를 열지 않았습니다(어긋남 ${Number(state?.worst || 0).toFixed(1)}px). 숫자로 고쳐 주세요.`;
  }
  if (reason === 'no-handles') {
    return '옮길 수 있는 자리가 없습니다 — 곡선 자체는 식이라 끌 수 없습니다(이름표는 옮길 수 있습니다).'
      + ' 그 밖에 점·선분·원·다각형·세로선·가로선과 호·부채꼴의 중심을 옮길 수 있습니다.';
  }
  return '';
}

/**
 * 생성이 **아무것도 만들지 않고** 돌아온 경우(200 `generated:false`)의 문구.
 * 실패가 아니다 — 모델이 「그림이 필요 없다」거나 「확정할 수 없다」고 본 것이고 본문은 그대로다.
 * reconvert 의 `converted:false` 와 같은 갈래라 말투를 맞춘다.
 */
export const FIGURE_NOT_GENERATED_TEXT =
  '이 글로는 그림을 확정할 수 없어 만들지 않았습니다 — 본문은 그대로입니다.';
